import React from "react";
import { motion, AnimatePresence } from "motion/react";
import { Plus, Check, Loader2, Search, Filter, ExternalLink } from "lucide-react";
import { DocumentsIcon } from "@/components/icons/nav";
import { UserPlusIcon } from "@/components/icons/actions";

interface Props {
  reducedMotion?: boolean;
}

const baseEmployees = [
  { name: "Marie Dubois", role: "Hôtesse de caisse", contract: "CDI · 35h", since: "Janv. 2023", dpae: "ok" },
  { name: "Lucas Bernard", role: "Polyvalent", contract: "CDI · 35h", since: "Mars 2023", dpae: "ok" },
  { name: "Sarah Mercier", role: "Adjointe", contract: "CDI · 30h", since: "Sept. 2022", dpae: "ok" },
];

const newEmployee = { name: "Julie Martin", role: "Hôtesse de caisse", contract: "CDD · 28h", since: "Mars 2025" };

const steps = [
  { label: "Identité", detail: "Julie Martin · née le 14/06/1998" },
  { label: "Contrat IDCC 1505", detail: "CDD niveau E2 · 12,31 €/h brut" },
  { label: "Temps de travail", detail: "28h/semaine · seuils heures sup configurés" },
  { label: "Récapitulatif", detail: "Génération du contrat PDF" },
];

export const RegistryScene: React.FC<Props> = ({ reducedMotion }) => {
  const [step, setStep] = React.useState(reducedMotion ? steps.length : 0);
  const [generating, setGenerating] = React.useState(false);
  const [done, setDone] = React.useState(reducedMotion);

  React.useEffect(() => {
    if (reducedMotion) return;
    const timeouts: ReturnType<typeof setTimeout>[] = [];
    timeouts.push(setTimeout(() => setStep(1), 700));
    timeouts.push(setTimeout(() => setStep(2), 1700));
    timeouts.push(setTimeout(() => setStep(3), 2700));
    timeouts.push(setTimeout(() => setStep(4), 3700));
    timeouts.push(setTimeout(() => setGenerating(true), 3900));
    timeouts.push(setTimeout(() => {
      setGenerating(false);
      setDone(true);
    }, 5300));
    return () => timeouts.forEach(clearTimeout);
  }, [reducedMotion]);

  return (
    <div className="px-3 md:px-6 pb-6 pt-3 space-y-3">
      {/* PageHero mock */}
      <div className="glass-subtle shadow-glass rounded-xl border border-border/50 px-4 py-3 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Console RH</div>
          <h1 className="text-[15px] font-bold text-foreground">Registre du personnel</h1>
        </div>
        <button className="h-7 px-3 rounded-md text-[11px] font-semibold flex items-center gap-1.5 bg-primary text-primary-foreground shadow-sm">
          <Plus className="w-3 h-3" />
          Nouveau salarié
        </button>
      </div>

      {/* Filters mock */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 h-7 px-2.5 rounded-md bg-card border text-[11px] text-muted-foreground">
          <Search className="w-3 h-3" />
          Rechercher un salarié…
        </div>
        <div className="flex items-center gap-1 h-7 px-2.5 rounded-md bg-card border text-[11px] text-muted-foreground">
          <Filter className="w-3 h-3" />
          Statut : Actifs
        </div>
        <span className="text-[10px] text-muted-foreground ml-auto">
          {done ? "4 salariés actifs" : "3 salariés actifs"}
        </span>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {/* Liste salariés */}
        <div className="bg-card border rounded-xl overflow-hidden shadow-sm">
          <div className="px-3 py-2 border-b bg-muted/15 text-[11px] font-semibold text-foreground flex items-center justify-between">
            Effectif
            <span className="text-[10px] text-muted-foreground font-normal">Trié par ancienneté</span>
          </div>
          <div className="divide-y">
            {baseEmployees.map((e) => (
              <div key={e.name} className="p-2.5 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-[11px] font-bold">
                  {e.name.split(" ").map((p) => p[0]).join("")}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-foreground truncate">{e.name}</div>
                  <div className="text-[10px] text-muted-foreground truncate">{e.role} · {e.contract}</div>
                </div>
                <span className="text-[9px] font-semibold text-success bg-success/10 px-1.5 py-0.5 rounded-full border border-success/20">
                  DPAE OK
                </span>
              </div>
            ))}
            <AnimatePresence>
              {done && (
                <motion.div
                  initial={{ opacity: 0, height: 0, x: -16 }}
                  animate={{ opacity: 1, height: "auto", x: 0 }}
                  className="p-2.5 flex items-center gap-3 bg-success/5"
                >
                  <div className="w-8 h-8 rounded-full bg-success/20 text-success flex items-center justify-center text-[11px] font-bold">
                    {newEmployee.name.split(" ").map((p) => p[0]).join("")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold text-foreground truncate flex items-center gap-1.5">
                      {newEmployee.name}
                      <span className="text-[9px] font-bold text-success bg-success/15 px-1.5 py-0.5 rounded-full">NOUVEAU</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground truncate">{newEmployee.role} · {newEmployee.contract}</div>
                  </div>
                  <span className="text-[9px] font-semibold text-warning bg-warning/10 px-1.5 py-0.5 rounded-full border border-warning/25">
                    DPAE à déclarer
                  </span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Wizard contrat */}
        <div className="bg-card border rounded-xl p-3.5 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
              <UserPlusIcon className="w-3.5 h-3.5 text-primary" />
            </div>
            <div>
              <h3 className="text-[12px] font-semibold text-foreground">Assistant contrat IDCC 1505</h3>
              <p className="text-[10px] text-muted-foreground">7 étapes guidées · sans saisie SSN</p>
            </div>
          </div>
          <div className="space-y-1.5">
            {steps.map((s, i) => {
              const isDone = i < step;
              const isActive = i === step && !done;
              return (
                <motion.div
                  key={s.label}
                  initial={{ opacity: 0.3 }}
                  animate={{ opacity: isDone || isActive ? 1 : 0.4 }}
                  className={`flex items-start gap-2.5 p-2 rounded-lg border ${
                    isActive ? "border-primary/40 bg-primary/5" : isDone ? "border-success/25 bg-success/5" : "border-border"
                  }`}
                >
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                    isDone ? "bg-success text-white" : isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                  }`}>
                    {isDone ? <Check className="w-2.5 h-2.5" /> : i + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-[11px] font-semibold text-foreground">{s.label}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{s.detail}</div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {generating && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-2.5 flex items-center gap-2 text-[10px] text-muted-foreground"
            >
              <Loader2 className="w-3 h-3 animate-spin text-primary" />
              Génération du contrat PDF…
            </motion.div>
          )}

          {done && (
            <div className="mt-2.5 space-y-1.5">
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-success/10 border border-success/25"
              >
                <DocumentsIcon className="w-3 h-3 text-success" />
                <span className="text-[10px] font-medium text-foreground">Contrat prêt — signature électronique disponible</span>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-warning/8 border border-warning/25"
              >
                <ExternalLink className="w-3 h-3 text-warning" />
                <span className="text-[10px] text-foreground">
                  <span className="font-semibold">Rappel DPAE</span> — à déclarer sur le site de l'URSSAF avant l'embauche
                </span>
              </motion.div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
